import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';

import { useResponsive } from '../../hooks/use-responsive';

import { HEADER } from '../layout/config-layout';
import Iconify from '../../theme/iconify/iconify';
import { mislecAPI } from '../../util/axios';

function Model({ updateUser, updateModel, selectedModel }) {
    const [model, setModel] = React.useState(selectedModel || null);
    const [models, setModels] = React.useState([]);
    const [fetched, setFetched] = React.useState(false);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const menu = Boolean(menuAnchor);
    const canSelectModel = models.length > 1;
    const selectedModelRef = React.useRef(selectedModel);
    const modelRef = React.useRef(model);

    const { t } = useTranslation();
    const lgUp = useResponsive('up', 'lg');

    React.useEffect(() => {
        selectedModelRef.current = selectedModel;
    }, [selectedModel]);

    React.useEffect(() => {
        modelRef.current = model;
    }, [model]);

    React.useEffect(() => {
        if (fetched) return;

        //  Fetch data from the server
        mislecAPI.get(`/api/models`)
            .then(response => {
                const fetchedModels = response.data.models || [];
                const preferredModel = selectedModelRef.current || modelRef.current;
                const modelExists = fetchedModels.some(option => option.name === preferredModel);

                if (fetchedModels.length > 0)
                    setModel(modelExists ? preferredModel : fetchedModels[0].name);

                setModels(fetchedModels);
                setFetched(true);
            })
            .catch(error => {
                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }, [fetched, updateUser]);

    React.useEffect(() => {
        if (!selectedModel || selectedModel === model) return;
        if (models.length === 0 || models.some(option => option.name === selectedModel))
            setModel(selectedModel);
    }, [selectedModel, model, models]);

    React.useEffect(() => {
        if (model)
            updateModel(model);
    }, [model, updateModel]);

    return (
        <>
            {lgUp ?
                <Stack
                    spacing={0.5}
                    sx={{
                        mt: 0,
                        ml: {
                            xs: 3,
                            md: 0
                        },
                        zIndex: 1111,
                        boxShadow: 'none',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: HEADER.H_MOBILE,
                        ...(lgUp && {
                            height: HEADER.H_DESKTOP,
                        })
                    }}
                >
                    <Grid
                        container
                        sx={{
                            width: 'fit-content !important',
                        }}
                    >
                        <ListItemButton
                            disableRipple
                            onClick={(event) => {
                                event.preventDefault();
                                if (!canSelectModel) return;
                                setMenuAnchor(event.currentTarget);
                            }}
                            sx={{
                                zIndex: '1111',
                                borderRadius: '8px',
                                padding: '6px',
                                backgroundColor: menu ? 'rgba(145, 158, 171, 0.08)' : 'transparent'
                            }}
                        >
                            <Iconify icon='lucide:bot' sx={{ mr: 1, mb: 0.5 }} width={24} />
                            <Typography variant='h6' sx={{ fontSize: '14px !important' }}>{model}</Typography>
                            {canSelectModel && <Iconify icon='mingcute:down-line' sx={{ height: '16px', width: '16px', ml: 1 }} />}
                        </ListItemButton>
                    </Grid>
                </Stack>
                :
                <Stack
                    spacing={0.5}
                    sx={{
                        mt: 0,
                        ml: 0,
                        px: 0,
                        zIndex: 1111,
                        boxShadow: 'none',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: HEADER.H_MOBILE,
                        ...(lgUp && {
                            height: HEADER.H_DESKTOP,
                        })
                    }}
                >
                    <Grid
                        container
                        sx={{
                            width: 'fit-content !important',
                        }}
                    >
                        <ListItemButton
                            disableRipple
                            onClick={(event) => {
                                event.preventDefault();
                                if (!canSelectModel) return;
                                setMenuAnchor(event.currentTarget);
                            }}
                            sx={{
                                pr: 0,
                                pl: 0.5,
                                zIndex: '1111',
                                borderRadius: '8px',
                                backgroundColor: menu ? 'rgba(145, 158, 171, 0.08)' : 'transparent'
                            }}
                        >
                            <Iconify icon='lucide:bot' width={24} />
                        </ListItemButton>
                    </Grid>
                </Stack>
            }
            {canSelectModel && <Menu
                id='mISLec_model_selection'
                anchorEl={menuAnchor}
                open={menu}
                onClose={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setMenuAnchor(null);
                }}
                sx={{
                    mb: 1
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: lgUp ? 'left' : 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: lgUp ? 'left' : 'center',
                }}
            >
                <MenuItem disabled>
                    <Typography variant='body2'>
                        {t('chat.model.title')}
                    </Typography>
                </MenuItem>

                {models.map((option, index) => (
                    <MenuItem
                        key={index}
                        onClick={(event) => {
                            event.preventDefault();
                            setMenuAnchor(null);
                            setModel(option.name);
                        }}
                    >
                        <Grid
                            container
                            alignItems='center'
                            sx={{
                                minWidth: {
                                    xs: '100%',
                                    md: '375px'
                                },
                                maxWidth: {
                                    xs: '100%',
                                    md: '375px'
                                }
                            }}
                        >
                            <Grid item xs={1} sx={{ display: 'flex' }}>
                                <ListItemIcon sx={{ minWidth: '100% !important', minHeight: '0px !important', mr: 1.5 }}>
                                    {option.type === 'api' && <Iconify icon='eos-icons:api-outlined' />}
                                    {option.type === 'local' && <Iconify icon='icon-park-outline:local' />}
                                </ListItemIcon>
                            </Grid>
                            <Grid item xs={10}>
                                <Grid container sx={{ pl: 1 }}>
                                    <Grid item xs={12}>
                                        <ListItemText className='mISLec_model_name' sx={{ fontSize: '0.85rem', fontWeight: '800 !important', whiteSpace: 'wrap' }}>
                                            {option.name}
                                        </ListItemText>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ListItemText sx={{ fontSize: '0.85rem', whiteSpace: 'wrap' }}>
                                            {option.description}
                                        </ListItemText>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={1}>
                                {option.name === model && (
                                    <IconButton disabled>
                                        <Iconify icon='eva:checkmark-outline' />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>
                    </MenuItem>
                ))}
            </Menu>}
        </>
    );
}

export default Model;
